import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Barcode, ArrowLeft, ShieldCheck } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg text-[#09090B]">
            <div className="w-8 h-8 rounded-md brand-primary flex items-center justify-center">
              <Barcode className="w-4 h-4 text-white" />
            </div>
            <span>MAGIC SIP</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="rounded-md border-zinc-300 h-9" data-testid="privacy-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Voltar
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-8 py-10">
        <div className="eyebrow mb-2 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#002FA7]" /> Compliance & LGPD
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B] mb-2">
          Política de Privacidade
        </h1>
        <p className="text-xs text-zinc-500 mb-8 font-mono">
          Última atualização: 18 de fevereiro de 2026
        </p>

        <article
          className="bg-white border border-zinc-200 rounded-md p-6 sm:p-8 space-y-6 text-sm text-zinc-700 leading-relaxed [&_h2]:font-display [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-[#09090B] [&_h2]:mt-6 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:space-y-1 [&_a]:text-[#002FA7] [&_a]:underline"
          data-testid="privacy-content"
        >
          <section>
            <h2>1. Quem somos</h2>
            <p>
              O <strong>Magic SIP</strong> (Search Images Products) é um utilitário SaaS que
              automatiza o cadastro em massa de produtos em plataformas de e-commerce brasileiras
              (Nuvemshop, Bling, Tiny, Omie) buscando imagens em catálogos públicos e gerando
              descrições/SEO por IA. Operado por Carlos Soglia (contato via WhatsApp{" "}
              <a href="https://w.app/carlos_soglia" target="_blank" rel="noreferrer">
                w.app/carlos_soglia
              </a>
              ).
            </p>
          </section>

          <section>
            <h2>2. Dados que coletamos</h2>
            <ul>
              <li>
                <strong>Dados de conta</strong>: nome, e-mail e senha (hash bcrypt) fornecidos no cadastro.
              </li>
              <li>
                <strong>Credenciais de ERP</strong>: access tokens OAuth do Nuvemshop, Bling, e chaves
                de API do Tiny/Omie. Armazenadas criptografadas com Fernet (chave privada em ambiente
                de servidor). Nunca são exibidas em texto puro na interface.
              </li>
              <li>
                <strong>Dados do lote</strong>: nomes de produto, categorias, preços e URLs de imagens
                que você envia ou que a IA gera. Ficam vinculados ao seu usuário.
              </li>
              <li>
                <strong>Dados de assinatura</strong>: histórico de pagamentos via Mercado Pago (tokens
                de assinatura, status de trial/ativo). Não armazenamos número de cartão — todos os
                dados sensíveis ficam no Mercado Pago (PCI-DSS).
              </li>
              <li>
                <strong>Logs técnicos</strong>: IP, timestamps e eventos de erro para diagnóstico. Retidos por 30 dias.
              </li>
            </ul>
          </section>

          <section>
            <h2>3. Dados que NÃO coletamos</h2>
            <ul>
              <li>
                <strong>Nenhuma PII de consumidores da sua loja</strong>. O Magic SIP jamais lê,
                grava ou processa pedidos, e-mails ou dados de clientes finais da sua loja Nuvemshop.
              </li>
              <li>Nenhum dado financeiro de terceiros além do necessário para a cobrança da sua assinatura.</li>
            </ul>
          </section>

          <section>
            <h2>4. Como usamos os dados</h2>
            <ul>
              <li>Buscar imagens em catálogos públicos (Open Food Facts, VTEX de varejistas brasileiros)</li>
              <li>Gerar descrições, SKUs, SEO e preços sugeridos via IA (Anthropic, Google e OpenAI através do Emergent LLM Key)</li>
              <li>Publicar produtos no ERP conectado sob seu comando explícito</li>
              <li>Enviar e-mails transacionais (boas-vindas, trial ending, past-due) via Resend</li>
              <li>Diagnosticar falhas e melhorar a qualidade das buscas</li>
            </ul>
          </section>

          <section>
            <h2>5. Compartilhamento com terceiros</h2>
            <p>Compartilhamos dados mínimos e apenas com processadores contratuais listados abaixo:</p>
            <ul>
              <li>
                <strong>Mercado Pago</strong> (Fintech Brasil) — processamento de pagamentos e assinaturas
              </li>
              <li>
                <strong>Nuvemshop / Bling / Tiny / Omie</strong> — ERPs de destino que você conectou
              </li>
              <li>
                <strong>Anthropic / Google / OpenAI</strong> — provedores de LLM (via Emergent) para
                geração de descrições. Não enviamos dados sensíveis, apenas nome+categoria do produto
              </li>
              <li>
                <strong>Resend</strong> (transactional email) — apenas nome e e-mail do lojista
              </li>
            </ul>
            <p>
              <strong>Não vendemos dados</strong> a anunciantes, corretores de dados ou terceiros.
            </p>
          </section>

          <section>
            <h2>6. LGPD Nuvemshop (webhooks obrigatórios)</h2>
            <p>Implementamos os 3 webhooks LGPD exigidos pela homologação Nuvemshop:</p>
            <ul>
              <li>
                <code className="bg-zinc-100 px-1.5 py-0.5 rounded font-mono text-xs">store/redact</code>
                {" "}— apagamos todos os dados vinculados a uma loja 48h após o lojista desinstalar o app
              </li>
              <li>
                <code className="bg-zinc-100 px-1.5 py-0.5 rounded font-mono text-xs">customers/redact</code>
                {" "}— registramos a solicitação (não armazenamos PII de consumidores, então nada a apagar)
              </li>
              <li>
                <code className="bg-zinc-100 px-1.5 py-0.5 rounded font-mono text-xs">customers/data_request</code>
                {" "}— informamos o lojista de que não retemos dados de consumidores finais
              </li>
            </ul>
          </section>

          <section>
            <h2>7. Seus direitos (LGPD)</h2>
            <p>Você pode a qualquer momento solicitar:</p>
            <ul>
              <li>Confirmação de que tratamos seus dados</li>
              <li>Acesso aos dados armazenados (portabilidade em JSON)</li>
              <li>Correção de dados incompletos, inexatos ou desatualizados</li>
              <li>Anonimização, bloqueio ou eliminação dos dados</li>
              <li>Revogação do consentimento (cancelamento da conta)</li>
            </ul>
            <p>
              Para exercer qualquer direito, entre em contato via{" "}
              <Link to="/support">página de suporte</Link> ou WhatsApp{" "}
              <a href="https://w.app/carlos_soglia" target="_blank" rel="noreferrer">
                w.app/carlos_soglia
              </a>
              . Resposta em até 15 dias úteis.
            </p>
          </section>

          <section>
            <h2>8. Segurança</h2>
            <ul>
              <li>Senhas armazenadas apenas como hash bcrypt (custo 12+)</li>
              <li>Credenciais de ERP criptografadas com Fernet em repouso</li>
              <li>Comunicação sempre em HTTPS/TLS</li>
              <li>JWT com expiração de 7 dias + rotação de secret</li>
              <li>Webhooks Mercado Pago e Nuvemshop validados por HMAC SHA-256</li>
            </ul>
          </section>

          <section>
            <h2>9. Retenção de dados</h2>
            <ul>
              <li>Conta ativa: dados mantidos enquanto a assinatura estiver ativa</li>
              <li>Após cancelamento: 30 dias para permitir reativação, depois exclusão automática</li>
              <li>Logs técnicos: 30 dias</li>
              <li>Registros fiscais (obrigação legal): 5 anos</li>
            </ul>
          </section>

          <section>
            <h2>10. Cookies</h2>
            <p>
              Usamos apenas cookies essenciais: token JWT de sessão (httpOnly) e localStorage para
              persistir o estado do batch entre navegações. Não usamos cookies de rastreamento ou
              analytics de terceiros.
            </p>
          </section>

          <section>
            <h2>11. Alterações nesta política</h2>
            <p>
              Podemos atualizar esta política ocasionalmente. Mudanças materiais serão notificadas
              por e-mail. A data de "Última atualização" no topo desta página reflete a versão vigente.
            </p>
          </section>

          <section>
            <h2>12. Contato</h2>
            <p>
              Dúvidas sobre esta política?
              <br />
              Suporte: <Link to="/support">/support</Link>
              <br />
              WhatsApp:{" "}
              <a href="https://w.app/carlos_soglia" target="_blank" rel="noreferrer">
                w.app/carlos_soglia
              </a>
              <br />
              E-mail: <a href="mailto:suporte@magicsearchimagesproducts.com">suporte@magicsearchimagesproducts.com</a>
            </p>
          </section>
        </article>
      </main>

      <footer className="border-t border-zinc-200 bg-white py-6 mt-10">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-xs text-zinc-500 font-mono flex justify-between flex-wrap gap-3">
          <span>MAGIC SIP · v1.0 · UTILITÁRIO PARA E-COMMERCE</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-[#002FA7]">Política de Privacidade</Link>
            <Link to="/support" className="hover:text-[#002FA7]">Suporte</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
